import { Badge, Button, ConfirmDialog, Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, PackageCheck, Ban } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { usePermission } from '../../../auth/auth-store';
import { formatDate, formatQty } from '../../../lib/format';
import { errorMessage, toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { purchaseOrdersApi, receiptsApi, receivingKeys } from '../api';
import { PO_STATUS_TONE, linePct } from '../format';

/** `/orders/purchase/:id` — lines with received progress + "Ta emot". */
export function PurchaseOrderDetailPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canReceive = usePermission('warehouse.goods.receive');
  const canManage = usePermission('orders.inbound.manage');
  const [cancelling, setCancelling] = useState(false);

  const query = useQuery({
    queryKey: receivingKeys.purchaseOrder(id),
    queryFn: () => purchaseOrdersApi.detail(id),
    enabled: id.length > 0,
  });
  useLiveQuery(['purchase-orders', 'receipts'], receivingKeys.purchaseOrder(id));

  const openReceipt = useMutation({
    mutationFn: () => receiptsApi.open({ poId: id }),
    onSuccess: async (receipt) => {
      toastSuccess(t('receipts.started'));
      await queryClient.invalidateQueries({ queryKey: ['receipts'] });
      void navigate(`/warehouse/receiving/${receipt.id}`);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const cancel = useMutation({
    mutationFn: () => purchaseOrdersApi.cancel(id),
    onSuccess: async () => {
      toastSuccess(t('po.detail.cancelled'));
      setCancelling(false);
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
    onError: (error) => {
      setCancelling(false);
      toastError(error);
    },
  });

  if (query.isLoading) return <FullPageSpinner />;
  if (query.error || !query.data) {
    return (
      <>
        <PageHeader title={t('po.title')} />
        <Surface className="p-6 text-sm text-danger-600">{errorMessage(query.error)}</Surface>
      </>
    );
  }

  const po = query.data;
  const receivable = po.status === 'open' || po.status === 'partially_received';

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/orders/purchase" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3.5" aria-hidden />
            {t('po.detail.back')}
          </Link>
        }
        title={po.poNo}
        subtitle={po.supplierName}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={PO_STATUS_TONE[po.status]} dot>
              {t(`po.statuses.${po.status}`)}
            </Badge>
            {po.openReceiptIds.length > 0 ? (
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate(`/warehouse/receiving/${po.openReceiptIds[0]!}`);
                }}
              >
                {t('po.detail.openReceipt')}
              </Button>
            ) : null}
            {canReceive && receivable ? (
              <Button
                icon={<PackageCheck className="size-4" />}
                loading={openReceipt.isPending}
                onClick={() => {
                  openReceipt.mutate();
                }}
              >
                {t('po.detail.receive')}
              </Button>
            ) : null}
            {canManage && po.status === 'open' ? (
              <Button
                variant="ghost"
                icon={<Ban className="size-4" />}
                onClick={() => {
                  setCancelling(true);
                }}
              >
                {t('po.detail.cancel')}
              </Button>
            ) : null}
          </div>
        }
      />

      <Surface className="mb-4 grid gap-4 p-4 sm:grid-cols-3">
        <Meta label={t('po.expectedDate')} value={formatDate(po.expectedDate)} />
        <Meta label={t('po.progress')} value={`${po.receivedPct}%`} />
        <Meta label={t('po.note')} value={po.note ?? '—'} />
      </Surface>

      <Surface className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <thead>
              <tr className="border-b border-surface-border">
                <Th>{t('po.detail.article')}</Th>
                <Th className="text-right">{t('po.detail.qty')}</Th>
                <Th>{t('po.detail.unit')}</Th>
                <Th className="text-right">{t('po.detail.qtyBase')}</Th>
                <Th className="text-right">{t('po.detail.received')}</Th>
                <Th className="text-right">{t('po.detail.open')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {po.lines.map((line) => (
                <Tr key={line.id}>
                  <Td>
                    <div className="flex flex-col">
                      <span className="text-ink">{line.articleName}</span>
                      <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">{formatQty(line.qty)}</Td>
                  <Td>{line.unitCode}</Td>
                  <Td className="text-right tabular-nums">{formatQty(line.qtyBase)}</Td>
                  <Td className="text-right tabular-nums">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-12 overflow-hidden rounded-full bg-surface-subtle" aria-hidden>
                        <div className="h-full rounded-full bg-brand-600" style={{ width: `${linePct(line)}%` }} />
                      </div>
                      {formatQty(line.receivedQtyBase)}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">{formatQty(line.openQtyBase)}</Td>
                </Tr>
              ))}
              {po.lines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-sm text-ink-muted">
                    {t('po.detail.noLines')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </Surface>

      {cancelling ? (
        <ConfirmDialog
          title={t('po.detail.cancelTitle')}
          message={t('po.detail.cancelBody')}
          confirmLabel={t('po.detail.cancel')}
          variant="danger"
          onClose={() => {
            setCancelling(false);
          }}
          onConfirm={() => cancel.mutateAsync().then(() => undefined)}
        />
      ) : null}
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-0.5 text-ink">{value}</div>
    </div>
  );
}
